/**
 * EquipmentSelectionTab - Bridge interface between Requirements Tab and Equipment Register
 * Phase 1: Basic structure with domain grouping and save functionality
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Package, AlertCircle, Loader2, Wand2 } from 'lucide-react';
import { getHardcodedVesselRequirements } from '../../../../config/hardcodedVesselRequirements';
import { useVessel } from '../../../../contexts/VesselContext';
import OverrideModal from './components/OverrideModal';
import QuantityControl from './components/QuantityControl';
// ExtrasDrawer removed - equipment added via Equipment Register only (Domain Spine v2)
import AbstractRequirementsSection from './components/AbstractRequirementsSection';
import DomainSection from './components/DomainSection';
import { 
  normalizeDomain, 
  DOMAIN_LABELS,
  groupRequirementsByDomain,
  calculateSelectionSummary,
  transformSelectionsToRegister
} from './utils/equipmentSelectionHelpers';
import { 
  saveUserEquipmentSelections, 
  transformSelectionsToRegister as newTransformSelectionsToRegister 
} from '../../../../services/equipment/selectionTransformer';
import { 
  getOptionalEquipmentFromVesselConfig,
  getAbstractRequirementsWithVariants 
} from '../../../../config/equipment/vesselConfigMapper';
import { ABSTRACT_REQUIREMENTS, getCurrentVariant } from '../../../../config/equipment/abstractRequirements';
import * as storage from '../../../../lib/storage';
import {
  createItemToggleHandler,
  createRequiredItemClickHandler,
  createOverrideConfirmHandler,
  createQuantityChangeHandler,
  createVariantSelectedHandler,
  createSaveHandler
} from './utils/equipmentSelectionHandlers';
// import { 
//   mergeAllEquipmentSources, 
//   getEquipmentSummary, 
//   DOMAIN_LABELS as MERGER_DOMAIN_LABELS
// } from '../wizard/steps/utils/equipmentDataMerger';

const EquipmentSelectionTab = ({ vesselConfig, wizardMode = false, wizardData = null, setActiveModule, setActiveModuleWithTab }) => {
  const { currentVesselId } = useVessel();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requirements, setRequirements] = useState(null);
  const [domainGroups, setDomainGroups] = useState({});
  const [abstractRequirements, setAbstractRequirements] = useState([]);
  const [optionalFromConfig, setOptionalFromConfig] = useState([]);
  const [wizardDomainGroups, setWizardDomainGroups] = useState({});
  const [useWizardSources, setUseWizardSources] = useState(wizardMode);
  const [error, setError] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideItem, setOverrideItem] = useState(null);
  
  // Context detection for Equipment Register redirect
  const [equipmentContext, setEquipmentContext] = useState(null);
  const [showGuidanceBanner, setShowGuidanceBanner] = useState(false);
  
  // Mount guard to prevent duplicate loads
  const hasMountedRef = useRef(false);
  const lastVesselTypeRef = useRef(null);

  // Load requirements only when vessel type changes or on mount
  useEffect(() => {
    const loadRequirements = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!vesselConfig) {
          throw new Error('Vessel configuration is required');
        }

        // Only reload if vessel configuration hasn't changed
        // CRITICAL: Must include galleyHeatSource because it affects gas detector requirements
        const vesselType = vesselConfig.vesselType || vesselConfig.category;
        const galleyConfig = `${vesselConfig.hasGalley}-${vesselConfig.galleyHeatSource}`;
        const configKey = `${vesselType}-${galleyConfig}`;
        if (hasMountedRef.current && lastVesselTypeRef.current === configKey) {
          setLoading(false);
          return; // Skip reload if config hasn't changed
        }



        // Load regular requirements from requirements engine
        const reqs = getHardcodedVesselRequirements(vesselConfig);

        // DEBUG: Check if gas detector is in requirements
        const gasDetector = reqs.equipment?.find(e => e.id === 'ffe_detector_gas_galley');

        setRequirements(reqs);
        
        // Load abstract requirements with current variants
        const abstractReqs = getAbstractRequirementsWithVariants(vesselConfig);

        setAbstractRequirements(abstractReqs);
        
        // Load optional equipment from vessel config
        const optionalEquipment = getOptionalEquipmentFromVesselConfig(vesselConfig);

        setOptionalFromConfig(optionalEquipment);

        // Load wizard equipment sources if in wizard mode
        // TODO: Re-enable when mergeAllEquipmentSources is restored
        if (useWizardSources && typeof mergeAllEquipmentSources !== 'undefined') {

          const configToUse = wizardData || vesselConfig;
          const wizardMergedData = mergeAllEquipmentSources(configToUse);

          setWizardDomainGroups(wizardMergedData);
        } else if (useWizardSources) {

          setWizardDomainGroups({});
        }
        
        // Mark as mounted and store vessel type
        hasMountedRef.current = true;
        lastVesselTypeRef.current = vesselType;
      } catch (err) {

        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadRequirements();
  }, [vesselConfig?.vesselType, vesselConfig?.category, vesselConfig?.galleyHeatSource, vesselConfig?.hasGalley, useWizardSources, wizardData]);

  // ExtrasDrawer restoration removed - user-added equipment now managed via Equipment Register only

  // Detect if user came from Equipment Register
  useEffect(() => {
    const contextData = sessionStorage.getItem('addEquipmentContext');

    if (contextData) {
      try {
        const context = JSON.parse(contextData);

        // Validate context
        if (context.returnTo === 'equipment-register' && context.vesselId === currentVesselId) {
          setEquipmentContext(context);
          setShowGuidanceBanner(true);

        } else {
          // Context is stale or for different vessel
          sessionStorage.removeItem('addEquipmentContext');
        }
      } catch (error) {

        sessionStorage.removeItem('addEquipmentContext');
      }
    }
  }, [currentVesselId]);

  // Group requirements by domain (including abstract requirements and optional equipment)
  const groupedEquipment = useMemo(() => {
    if (useWizardSources && Object.keys(wizardDomainGroups).length > 0) {
      // Use wizard merged sources

      // Transform wizard format to match expected format
      const transformedGroups = {};
      
      Object.keys(wizardDomainGroups).forEach(domain => {
        const wizardDomain = wizardDomainGroups[domain];
        transformedGroups[domain] = {
          required: wizardDomain.required || [],
          optional: wizardDomain.optional || [], 
          wizardSelected: wizardDomain.wizardSelected || [],
          abstract: [] // TODO: Integrate abstract requirements with wizard data
        };
      });
      
      // Still add abstract requirements to wizard data
      abstractRequirements.forEach(abstractReq => {
        const domain = abstractReq.domain;
        if (!transformedGroups[domain]) {
          transformedGroups[domain] = { required: [], optional: [], wizardSelected: [], abstract: [] };
        }
        transformedGroups[domain].abstract.push(abstractReq);
      });
      
      return transformedGroups;
    }
    
    // Use legacy requirements engine (original logic)
    const groups = groupRequirementsByDomain(requirements);
    
    // Filter out regular requirements that are replaced by abstract requirements
    const abstractRequirementIds = abstractRequirements.map(req => req.id);
    const replacedRequirementIds = new Set();
    
    // Define which regular requirements are replaced by abstract requirements
    abstractRequirements.forEach(abstractReq => {
      // Currently no regular requirements are replaced by abstract requirements
      // Keep this structure for future abstract requirements that DO replace regular ones
    });
    
    // Remove replaced requirements from regular requirements
    Object.keys(groups).forEach(domain => {
      if (groups[domain].required) {
        groups[domain].required = groups[domain].required.filter(req => 
          !replacedRequirementIds.has(req.id)
        );
      }
    });
    
    // Process abstract requirements - convert to required items if variants are selected
    abstractRequirements.forEach(abstractReq => {
      const domain = abstractReq.domain;
      if (!groups[domain]) {
        groups[domain] = { required: [], optional: [] };
      }
      
      // Check if this abstract requirement has a selected variant
      const currentVariant = getCurrentVariant(abstractReq.id, vesselConfig);
      
      if (currentVariant) {
        // Convert abstract requirement to required equipment item
        const concreteEquipment = {
          id: `${abstractReq.id}_${currentVariant.id}`,
          name: currentVariant.name,
          category: abstractReq.category,
          domain: abstractReq.domain,
          reference: abstractReq.reference || 'WBC3',
          mandatory: true,
          selected: true,
          qtyPresent: abstractReq.qtyRequired === 'pob_count' ? parseInt(vesselConfig.maxPersons) || 1 : abstractReq.qtyRequired || 1,
          qtyRequired: abstractReq.qtyRequired === 'pob_count' ? parseInt(vesselConfig.maxPersons) || 1 : abstractReq.qtyRequired || 1,
          description: currentVariant.description || abstractReq.description,
          source: 'wizard_selection',
          abstractRequirementId: abstractReq.id,
          variantId: currentVariant.id
        };
        
        // Add to required items (not abstract)
        groups[domain].required.push(concreteEquipment);

      }
      // If no variant selected, don't add it at all - Step 5 should handle all required selections
    });
    
    // Add equipment from vessel config (both mandatory and optional)
    optionalFromConfig.forEach(equipmentItem => {
      const domain = equipmentItem.domain;
      if (!groups[domain]) {
        groups[domain] = { required: [], optional: [] };
      }
      
      // Check if this item is mandatory - if so, add to required section
      if (equipmentItem.mandatory) {
        groups[domain].required.push(equipmentItem);
      } else {
        groups[domain].optional.push(equipmentItem);
      }
    });
    
    
    return groups;
  }, [requirements, abstractRequirements, optionalFromConfig, useWizardSources, wizardDomainGroups]);

  // Update local state only when grouping actually changes
  useEffect(() => {
    setDomainGroups(groupedEquipment);
  }, [groupedEquipment]);

  // Create handlers using extracted functions
  const handleItemToggle = createItemToggleHandler(setDomainGroups);
  const handleRequiredItemClick = createRequiredItemClickHandler(setOverrideItem, setShowOverrideModal);
  const handleOverrideConfirm = (overrideData) => createOverrideConfirmHandler(setDomainGroups, setShowOverrideModal, setOverrideItem)(overrideData, overrideItem);
  const handleQuantityChange = createQuantityChangeHandler(setDomainGroups, setOverrideItem, setShowOverrideModal);

  // Handle variant selection for abstract requirements
  const handleVariantSelected = createVariantSelectedHandler(setDomainGroups);

  // Calculate selection summary
  const selectionSummary = useMemo(() => {
    return calculateSelectionSummary(domainGroups);
  }, [domainGroups]);


  // Handle save to equipment register using new selection system
  const handleSave = async () => {
    try {
      setSaving(true);

      if (!currentVesselId) {
        throw new Error('No vessel selected');
      }

      // Collect selections in the new format
      const requiredSelections = [];
      const optionalSelections = [];

      // DEBUG: Check if gas detector is in domainGroups at save time
      const gasDetectorAtSave = domainGroups?.ffe?.required?.find(e => e.id === 'ffe_detector_gas_galley');


      Object.entries(domainGroups).forEach(([domain, group]) => {
        // Process required items
        if (group.required) {
          group.required.forEach(item => {
            requiredSelections.push({
              id: item.id,
              name: item.name,
              selected: true, // Required items are always selected
              qtyPresent: item.qtyPresent || 1,
              qtyRequired: item.qtyRequired || 1,
              selectedAt: new Date().toISOString(),
              domain: domain,
              location: item.location
            });
          });
        }

        // Process optional items
        if (group.optional) {
          group.optional.forEach(item => {
            optionalSelections.push({
              id: item.id,
              name: item.name,
              selected: item.selected || false,
              qtyPresent: item.selected ? (item.qtyPresent || 1) : 0,
              qtyRequired: 0, // Optional items have no required quantity
              selectedAt: item.selected ? new Date().toISOString() : null,
              domain: domain,
              location: item.location
            });
          });
        }
      });

      const selections = {
        required: requiredSelections,
        optional: optionalSelections
      };

      // Save user selections
      const saveSuccess = saveUserEquipmentSelections(currentVesselId, selections);

      if (!saveSuccess) {
        throw new Error('Failed to save user selections');
      }

      // CRITICAL FIX: Read-Merge-Write Pattern to preserve user-added equipment
      // STEP 1: Read existing equipment register from localStorage
      const storageKey = `equipment_register_${currentVesselId}`;
      let existingItems = [];

      try {
        const existingData = localStorage.getItem(storageKey);
        if (existingData) {
          existingItems = JSON.parse(existingData);

        }
      } catch (parseError) {

        // Continue with empty array if parse fails
      }

      // STEP 2: Filter to preserve ONLY user-added items (accept both 'added' and 'user_added' for backward compatibility)
      const userAddedItems = existingItems.filter(item => {
        return item.source === 'added' || item.source === 'user_added';
      });

      // STEP 3: Generate equipment items from current selections

      const derivedItems = newTransformSelectionsToRegister(selections, currentVesselId, vesselConfig);

      // STEP 4: Merge user-added items with derived items
      const mergedItems = [...derivedItems, ...userAddedItems];

      // STEP 5: Write merged result back to localStorage
      try {
        storage.writeJSON(storageKey, mergedItems);

        // STEP 6: Dispatch sr:storage:set event so useStorageState hooks in other components
        // (like EquipmentRecordsTab) pick up the change. Without this, the hook's React state
        // doesn't update because localStorage changes within the same tab don't trigger the
        // native 'storage' event.
        window.dispatchEvent(new CustomEvent('sr:storage:set', {
          detail: {
            module: 'equipment_register',
            vesselId: currentVesselId,
            key: storageKey,
            value: mergedItems,
            timestamp: Date.now()
          }
        }));

        // Also trigger standard storage event for broader compatibility
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new StorageEvent('storage', {
            key: storageKey,
            newValue: JSON.stringify(mergedItems),
            storageArea: localStorage
          }));
        }
      } catch (e) {

        throw new Error('Failed to save equipment register. Please try again or contact support.');
      }

      // Also emit event for any listeners that might be mounted (e.g., when Equipment tab is visible)

      window.dispatchEvent(new CustomEvent('equipment:selections:saved', {
        detail: {
          vesselId: currentVesselId,
          source: 'user_selections',
          timestamp: Date.now()
        }
      }));

      alert(`Equipment selections saved! Equipment register will update automatically.`);
      
      // Handle return to Equipment Register if context exists
      if (equipmentContext && equipmentContext.returnTo === 'equipment-register') {

        // Set success flags for Equipment Register to detect
        sessionStorage.setItem('equipment:justAdded', 'true');
        sessionStorage.setItem('equipment:addedTab', equipmentContext.tab || 'all');

        // Clean up the context
        sessionStorage.removeItem('addEquipmentContext');

        // Navigate back after showing success message (1.5 seconds)
        setTimeout(() => {
          // Use app's navigation system (NOT react-router)
          if (setActiveModuleWithTab) {
            setActiveModuleWithTab('maintenance', 'register');
          } else if (setActiveModule) {
            setActiveModule('maintenance');
          } else {
            // Fallback to custom event

            window.dispatchEvent(new CustomEvent('navigation:returnToEquipment', {
              detail: { tab: equipmentContext.tab }
            }));
          }
        }, 1500);
      }
      
    } catch (error) {

      alert(`Error saving equipment: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Guidance banner when coming from Equipment Register
  const GuidanceBanner = () => {
    if (!showGuidanceBanner || !equipmentContext) return null;

    return (
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <Package className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-blue-900 font-medium">Add Equipment via Domain Selection</h3>
            <p className="text-blue-700 text-sm mt-1">
              Select equipment from the domain sections below, or use "+ Add Extras" to add custom equipment.
              All equipment added here will be properly tracked for maintenance and compliance.
            </p>
            <p className="text-blue-600 text-sm mt-2">
              <strong>Remember to click "Save Equipment Selection" at the bottom when you're done.</strong>
            </p>
          </div>
          <button
            onClick={() => {
              setShowGuidanceBanner(false);
              // Don't remove context yet - still need it for return flow
            }}
            className="text-blue-600 hover:text-blue-800 ml-3"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
        <span className="ml-3 text-gray-600">Loading equipment requirements...</span>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="ml-2 text-red-800 font-medium">Error loading requirements</span>
        </div>
        <p className="mt-2 text-red-700">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // === NON-SELF-PROPELLED PLATFORM DETECTION ===
  // Show platform-specific view for non-self-propelled platforms
  const isNonSelfPropelledPlatform = vesselConfig?.vesselType === 'non_self_propelled' || 
                                     vesselConfig?.baseCertificate === 'non_self_propelled' ||
                                     vesselConfig?.type === 'non_self_propelled';






  if (isNonSelfPropelledPlatform) {
    return (
      <div className="space-y-6">
        {/* Platform Header */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start">
            <Package className="h-5 w-5 text-purple-600 mt-1" />
            <div className="ml-3">
              <h3 className="text-purple-900 font-medium">Non-Self-Propelled Platform Equipment</h3>
              <p className="text-purple-700 text-sm mt-1">
                This platform follows WBC3 Section 26.5.1.1 selective assessment framework. 
                Only minimal platform-specific equipment is required.
              </p>
            </div>
          </div>
        </div>

        {/* Platform-Specific Equipment List */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-900">Platform Safety Equipment</h4>
            <p className="text-sm text-gray-600 mt-1">Required equipment for non-self-propelled working platforms</p>
          </div>
          
          <div className="divide-y divide-gray-200">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-gray-900">Emergency Towline (Pre-rigged)</h5>
                  <p className="text-sm text-gray-600">WBC3 Section 26.4.4</p>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  MANDATORY
                </span>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-gray-900">Emergency Anchor and Cable</h5>
                  <p className="text-sm text-gray-600">WBC3 Section 26.4.4</p>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  MANDATORY
                </span>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-gray-900">Emergency Lifebuoys with Lines (2)</h5>
                  <p className="text-sm text-gray-600">WBC3 Section 26.4.4</p>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  MANDATORY
                </span>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-gray-900">Forward End Marking (2000mm x 150mm white bars)</h5>
                  <p className="text-sm text-gray-600">WBC3 Section 26.5.2.3</p>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  MANDATORY
                </span>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-gray-900">Load Line Marking (excluding draught marks)</h5>
                  <p className="text-sm text-gray-600">WBC3 Section 26.5.2.2</p>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  MANDATORY
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* What's NOT Required Notice */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h4 className="text-gray-900 font-medium mb-3">Items NOT Required for Platforms</h4>
          <div className="text-sm text-gray-700 space-y-2">
            <p>✗ <strong>Universal LSA equipment:</strong> No lifejackets, liferafts, or personal survival equipment (unmanned platform)</p>
            <p>✗ <strong>Fire-fighting equipment:</strong> No fire extinguishers or detection systems (working platform)</p>
            <p>✗ <strong>Navigation systems:</strong> No GPS, radar, or chart equipment (towed platform)</p>
            <p>✗ <strong>Communication systems:</strong> No radio or GMDSS equipment (towed platform)</p>
            <p>✗ <strong>Electrical systems:</strong> No power systems or electrical equipment (basic platform)</p>
            <p>✗ <strong>Operational equipment:</strong> No winches or lifting gear (depends on specific operation)</p>
          </div>
        </div>

        {/* Platform Compliance Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="text-blue-900 font-medium mb-2">Platform Compliance Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">5</div>
              <div className="text-sm text-gray-600">Total Requirements</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">5</div>
              <div className="text-sm text-gray-600">Mandatory Items</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">0</div>
              <div className="text-sm text-gray-600">Optional Items</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">100%</div>
              <div className="text-sm text-gray-600">Compliance</div>
            </div>
          </div>
          <p className="text-sm text-blue-800 mt-4">
            <strong>Additional equipment may be required</strong> depending on specific operational conditions, 
            location of use, and project requirements. Consult with your project management team.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Guidance banner */}
      <GuidanceBanner />
      
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <Package className="h-5 w-5 text-blue-600 mt-1" />
            <div className="ml-3">
              <h3 className="text-blue-900 font-medium">WBC3 Equipment Requirements</h3>
              <p className="text-blue-700 text-sm mt-1">
                This shows the equipment your vessel requires under WBC3. Use the <strong>Equipment Register</strong> to add and manage your actual equipment.
              </p>
            </div>
          </div>
          
          {/* Equipment Sources Info */}
          {!wizardMode && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setUseWizardSources(!useWizardSources)}
                className={`flex items-center px-3 py-1 text-sm rounded-md transition-colors ${
                  useWizardSources
                    ? 'bg-cyan-600 text-white'
                    : 'bg-white text-cyan-600 border border-cyan-200 hover:bg-cyan-50'
                }`}
                title={useWizardSources ? 'Show requirements engine only' : 'Include wizard auto-selections'}
              >
                <Wand2 className="h-4 w-4 mr-1" />
                {useWizardSources ? 'Enhanced' : 'Basic'}
              </button>
            </div>
          )}
        </div>
        
        {/* Enhanced Mode Indicator */}
        {(useWizardSources || wizardMode) && (
          <div className="mt-3 p-2 bg-cyan-100 border border-cyan-200 rounded-md">
            <div className="flex items-center text-sm text-cyan-800">
              <Wand2 className="h-4 w-4 mr-2" />
              <span className="font-medium">Enhanced Selection:</span>
              <span className="ml-2">Showing equipment from vessel configuration, operations triggers, GMDSS matrix, and regulatory requirements</span>
            </div>
          </div>
        )}
      </div>

      {/* Domain Sections */}
      <div className="space-y-4">
        {Object.keys(domainGroups).map(domain => (
          <DomainSection
            key={domain}
            domain={domain}
            domainData={domainGroups[domain]}
            domainLabel={DOMAIN_LABELS[domain] || domain.toUpperCase()}
            onRequiredItemClick={handleRequiredItemClick}
            onItemToggle={handleItemToggle}
            onQuantityChange={handleQuantityChange}
          />
        ))}
      </div>
      

      {/* FT-53: Reference Only Notice - replaces save button that bulk-populated equipment from config */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Package className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-800">
              <strong>Reference Only</strong> — This tab shows what WBC3 requires for your vessel configuration.
              To add equipment to your register, go to <strong>Equipment Register → Add Equipment</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Override Requirement Modal */}
      {showOverrideModal && overrideItem && (
        <OverrideModal
          item={overrideItem.item}
          domain={overrideItem.domain}
          onConfirm={handleOverrideConfirm}
          onCancel={() => {
            setShowOverrideModal(false);
            setOverrideItem(null);
          }}
        />
      )}

    </div>
  );
};

export default EquipmentSelectionTab;